import { NervaUserData, TrialPlan } from 'hydra-data-types';
import { ActionType } from '../../../store/ActionTypes';
import { StoreAction } from '../../../store/Store';
import { Environment } from '../../../util/Environment';
import { calculatePapScore, PapScoreResult, PapTier } from './PapScoring';

const PAP_COUPON_CODES = {
  [PapTier.Moderate]: { prod: 'PAPWA50', dev: 'PAP50DEV' },
  [PapTier.Full]: { prod: 'PAPWA100', dev: 'PAP100DEV' },
} as const;

const PAP_ONEOFF_STANDARD_COUPON = { prod: 'PAPONEOFF25', dev: 'PAPONEOFF25DEV' } as const;

type PapCouponCode =
  | (typeof PAP_COUPON_CODES)[keyof typeof PAP_COUPON_CODES]['prod' | 'dev']
  | (typeof PAP_ONEOFF_STANDARD_COUPON)['prod' | 'dev'];

function papCouponCode(tier: PapTier.Moderate | PapTier.Full): PapCouponCode {
  return Environment.PRODUCTION ? PAP_COUPON_CODES[tier].prod : PAP_COUPON_CODES[tier].dev;
}

function oneOffStandardCouponCode(): PapCouponCode {
  return Environment.PRODUCTION ? PAP_ONEOFF_STANDARD_COUPON.prod : PAP_ONEOFF_STANDARD_COUPON.dev;
}

type TierConfig = {
  content: string[];
  couponCode?: PapCouponCode;
  discount: `${25 | 50 | 100}%`;
  buttonTitle: 'Apply discount' | 'Continue';
};

const PAP_TIERS: Record<PapTier, TierConfig> = {
  [PapTier.Standard]: {
    content: [
      'Thank you for completing the assessment.',
      'Based on your responses, you qualify for our standard patient assistance: 25% discount.',
    ],
    // Referred patients by default get 25% off so we don't need to dispatch another code as it's already applied.
    discount: '25%',
    buttonTitle: 'Continue',
  },
  [PapTier.Moderate]: {
    content: [
      'Thank you for completing the assessment.',
      "Based on your responses, you qualify for additional support. You'll receive a 50% discount through our Patient Assistance Program.",
    ],
    couponCode: papCouponCode(PapTier.Moderate),
    discount: '50%',
    buttonTitle: 'Apply discount',
  },
  [PapTier.Full]: {
    content: [
      'Thank you for completing the assessment.',
      'Based on your responses, you qualify for full coverage at no cost.',
    ],
    couponCode: papCouponCode(PapTier.Full),
    discount: '100%',
    buttonTitle: 'Apply discount',
  },
};

function getNonReferredTierConfig(tier: PapTier): TierConfig {
  const base = PAP_TIERS[tier];
  if (tier === PapTier.Standard) {
    return { ...base, couponCode: oneOffStandardCouponCode(), buttonTitle: 'Apply discount' };
  }
  return base;
}

export type PapResult = PapScoreResult & { tierConfig: TierConfig };

/** The score, tier and tier copy/discount for the user's PAP answers. */
export function getPapResult(userData: NervaUserData, isNonReferred: boolean): PapResult {
  const { tier, score } = calculatePapScore(userData, isNonReferred);
  const tierConfig = isNonReferred ? getNonReferredTierConfig(tier) : PAP_TIERS[tier];
  return { tier, score, tierConfig };
}

/** What Continue dispatches: the result, the tier's coupon, and no trial for full coverage. */
export function getPapContinueActions(result: PapResult): StoreAction[] {
  const { score, tier, tierConfig } = result;
  return [
    { type: ActionType.SET_PAP_SCORE_RESULT, scoreResult: { score, tier } },
    ...(tierConfig.couponCode === undefined
      ? []
      : [{ type: ActionType.SET_COUPON_CODE, couponCode: tierConfig.couponCode } as const]),
    ...(tier === PapTier.Full
      ? [{ type: ActionType.SET_SELECTED_TRIAL_PLAN, trialPlan: TrialPlan.None } as const]
      : []),
  ];
}
